import { BrandAnalysisComponent } from "src/app/modules/brand-analysis/brand-analysis/brand-analysis.component";

export const AD_TRACKING_STUDY_NAMES = [
  "NorthStar 2.0 - Ad Tracking Performance"
];

export const METRIC_DECIMAL_PLACES = 2;

export function isAdTrackingStudy(studyName: string | null | undefined): boolean {
  return !!studyName && AD_TRACKING_STUDY_NAMES.includes(studyName);
}

export function metricDecimalPlaces(studyName: string | null | undefined): number {
  return isAdTrackingStudy(studyName) ? METRIC_DECIMAL_PLACES : 1;
}

export function roundHalfUp(value: number, decimals: number): number {
  if (!Number.isFinite(value)) {
    return value;
  }

  let str = String(value);
  const neg = str.startsWith('-');
  if (neg) {
    str = str.slice(1);
  }
  if (!str.includes('.')) {
    str += '.';
  }

  const [intPart, decPartRaw] = str.split('.');
  const decPart = decPartRaw.padEnd(decimals + 1, '0');
  const keep = decPart.slice(0, decimals);
  const nextDigit = decPart.charCodeAt(decimals) - 48;

  const digits = (intPart + keep).split('').map(Number);
  if (nextDigit >= 5) {
    let i = digits.length - 1;
    while (i >= 0) {
      digits[i]++;
      if (digits[i] === 10) {
        digits[i] = 0;
        i--;
      } else {
        break;
      }
    }
    if (i < 0) {
      digits.unshift(1);
    }
  }

  const resultStr = digits.join('');
  const intLen = resultStr.length - decimals;
  const result = resultStr.slice(0, intLen) + (decimals > 0 ? '.' + resultStr.slice(intLen) : '');
  return Number((neg ? '-' : '') + result);
}

export function roundMetricValue(value: number, decimals: number, studyName: string | null | undefined): number {
  if (!isAdTrackingStudy(studyName)) {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }
  return roundHalfUp(value, decimals);
}

export function formatMetricDisplay(
  value: number | string | null | undefined,
  studyName: string | null | undefined
): string {
  if (value == null || value === '') {
    return value == null ? '' : String(value);
  }

  const raw = String(value).trim();
  if (
    raw === 'NA' ||
    raw === '(NA)' ||
    raw === 'I/S' ||
    raw === 'LS' ||
    raw === 'Not Applicable'
  ) {
    return raw;
  }

  if (!isAdTrackingStudy(studyName)) {
    return raw;
  }

  const match = raw.match(/^([+-]?(?:\d+\.?\d*|\.\d+))(.*)$/);
  if (!match) {
    return raw;
  }

  const num = Number(match[1]);
  if (!Number.isFinite(num)) {
    return raw;
  }

  return roundHalfUp(num, METRIC_DECIMAL_PLACES).toFixed(METRIC_DECIMAL_PLACES) + match[2];
}

export function brandAnalysisLabel(studyName: string | null | undefined): string {
  return isAdTrackingStudy(studyName) ? "Ad Analysis" : "Brand Analysis";
}

export function brandAnalysisRoute(studyName: string | null | undefined): string {
  return isAdTrackingStudy(studyName) ? 'AdAnalysis' : 'BrandAnalysis';
}

export function snapshotTypeDisplayLabel(
  studyName: string | null | undefined,
  snapshotTypeName: string | null | undefined
): string {
  if (!isAdTrackingStudy(studyName) || !snapshotTypeName) {
    return snapshotTypeName ?? '';
  }
  switch (snapshotTypeName) {
    case 'Single Brand':
      return 'Single Ad';
    case 'Multiple Brands':
      return 'Multiple Ads';
    default:
      return snapshotTypeName;
  }
}

export function brandEntityLabel(studyName: string | null | undefined): string {
  return isAdTrackingStudy(studyName) ? 'Ad' : 'Brand';
}

export function brandsEntityLabel(studyName: string | null | undefined): string {
  return isAdTrackingStudy(studyName) ? 'Ads' : 'Brands';
}

export function brandSelectionLabel(studyName: string | null | undefined): string {
  return isAdTrackingStudy(studyName) ? 'Ad Selection' : 'Brand Selection';
}

export function brandIndependentLabel(studyName: string | null | undefined): string {
  return isAdTrackingStudy(studyName) ? 'Ad Independent' : 'Brand Independent';
}

export function brandDependentLabel(studyName: string | null | undefined): string {
  return isAdTrackingStudy(studyName) ? 'Ad Dependent' : 'Brand Dependent';
}

export function swapBrandWordsForDisplay(text: string | null | undefined, studyName: string | null | undefined): string {
  if (!text || !isAdTrackingStudy(studyName)) {
    return text ?? '';
  }
  return text
    .replace(/\bBrands\b/g, 'Ads')
    .replace(/\bBrand\b/g, 'Ad');
}

export const COMMON_CONSTANTS = {
  NestingConfiguration: 2,
  InsufficientBase_Sample: 15,
  LowBase_Sample: 30,
  SigLowerLimit_95: -1.96,
  SigUpperLimit_95: 1.96,
  SigLowerLimit_90: -1.65,
  SigUpperLimit_90: 1.65,
  SigLowerLimit_98: -2.33,
  SigUpperLimit_98: 2.33,
  SigLowerLimit_99: -2.576,
  SigUpperLimit_99: 2.576,
  BrandAnalysisModuleId: 6,
  Configuration: 998,
  SnapshotModuleId: 2,
  DataExplorerModuleId: 1,
  DataUploadModuleId: "Data Upload",
  CrossTabModuleId: "Crosstab",
  BrandAnalysisModuleNameId: "Brand Analysis",

  //ModuleNames
  DataUploadModuleName: "Data Upload",
  CrossTabModuleName: "Crosstab",
  BrandAnalysisModuleName: "Brand Analysis",
  ARKName: "ARK",
  SnapShotModuleName: "Snapshot",
  DataUploadName: "Data Upload",
  AlertName: "Alert",


  powerBIurl:
    'https://app.powerbi.com/reportEmbed?reportId=fe44bd33-c58e-4b4e-83fc-45ffaaf97f3f&appId=e4d78180-0859-4013-96fc-3d87815368b8&autoAuth=true&ctid=1e355c04-e0a4-42ed-8e2d-7351591f0ef1&filterPaneEnabled=false&navContentPaneEnabled=false',
};

export const COMMON_CONSTANTS_SETTING = {
UserManagement: "userManagement",
ProjectManagement: "projectManagement",
DataUpload: "dataUpload",
ExportDownload: "exportDownload",
HelpGuide: "helpGuide",
SnapshotViews: "snapshotViews",
ReportIssue: "reportIssue",
Logout: "logout",
DocumentRepository: "documentRepository"
}

export const COMMON_CONSTANTS_SNAPSHOT = {
  NestingConfiguration: 2,
  InsufficientBase_Sample: 15,
  LowBase_Sample: 30,
  SigLowerLimit_95: -1.96,
  SigUpperLimit_95: 1.96,
  SigLowerLimit_90: -1.65,
  SigUpperLimit_90: 1.65,
  SigLowerLimit_98: -2.33,
  SigUpperLimit_98: 2.33,
  SigLowerLimit_99: -2.576,
  SigUpperLimit_99: 2.576,
  SigUpperLimit_None: 0,
  SigLowerLimit_None: 0,
  BrandAnalysisModuleId: 6,
  Configuration: 998,
  SnapshotModuleId: 2,
  DataExplorerModuleId: 1,
  powerBIurl:
    'https://app.powerbi.com/reportEmbed?reportId=fe44bd33-c58e-4b4e-83fc-45ffaaf97f3f&appId=e4d78180-0859-4013-96fc-3d87815368b8&autoAuth=true&ctid=1e355c04-e0a4-42ed-8e2d-7351591f0ef1&filterPaneEnabled=false&navContentPaneEnabled=false',
};
export const COMMON_CONSTANTS_Percentage = {
  ConfidencePercent_90: '90%',
  ConfidencePercent_95: '95%',
  ConfidencePercent_98: '98%',
  ConfidencePercent_99: '99%',
  ConfidencePercent_None: 'None'

};

export const BUTTON_HOVER_NAMES = {
  Filtering: `User can see shares on selected Total, the metric in 'Rows' are FILTERED on the metrics selected in 'Row Nesting'.`,
  Profiling: `This allows user to see shares of Nested groups on Parent Metric. Here, Metrics selected in 'Rows' are PROFILED by the metrics selected in 'Row Nesting'.`,
  Answer_Base: `All the Metrics selected in the Filters and Columns along with an additional filter of Respondents who have answered that particular question(in Row) constitutes the base`,
  Total_Base: `All the Metrics selected in the Filters and Columns constitute the base`,
  Load_Selection: `Auto generate saved selection`,
  Custom_Groups: `Define new metrics using existing variables`,
  Custom_Report: `Combine saved selections into a report`

}

export interface CheckboxSelection {
  id: number;
  selected: boolean;
  name: string;
  value: string;
}

export class CheckboxSelectionModel {
  static additionalCheckBoxSelection: CheckboxSelection[] = [
    { id: 1, selected: false, name: 'Ranking', value: 'Ranking' },
    { id: 2, selected: false, name: 'Sticky Selection', value: 'Sticky_Selection' },
    { id: 3, selected: false, name: 'Help Guide', value: 'Help_Guide' },
    { id: 4, selected: false, name: 'Static Time Frame', value: 'Static_Time_Frame' },
    { id: 5, selected: false, name: 'AI Integration', value: 'AI_Integration' },
    { id: 6, selected: false, name: 'Entry points _ Multi Select', value: 'Entry_points_Multi_Select' },
  ];

}

export const exceptionMessage =
  `This dashboard/module is specifically designed to support **{{client}}** - **{{study}}** and its predefined parameters.<br>` +
  `It does not support general knowledge queries or incomplete requests that fall outside the scope.<br>` +
  `Please **rephrase your query** to align with the available parameters and features of the **{{client}}** - **{{study}}** for relevant results.`;


